import { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { initializeApp, deleteApp } from "firebase/app";
import { getAuth, createUserWithEmailAndPassword } from "firebase/auth";
import {
  doc,
  setDoc,
  addDoc,
  collection,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db, firebaseConfig } from "../../../firebase";
import styles from "../../../styles/AdminConsole.module.css";
import { COUNTRIES } from "../../../data/constants";
import StatCard from "../shared/components/StatCard";
import SelectWrapper from "../shared/components/SelectWrapper";
import Toggle from "../shared/components/Toggle";
import Pagination from "../shared/components/Pagination";
import CategoryBadge from "../shared/components/CategoryBadge";
import Modal from "../shared/components/Modal";

const ITEMS_PER_PAGE = 15;

// Blank order profile seeded on supplier creation so their PLM loads immediately
const blankOrderProfile = (user = {}) => ({
  orderNumber: "",
  orderMonth: "",
  orderValueUSD: 0,
  orderValueINR: "",
  incoterm: "",
  skuCount: 0,
  skuBreakdown: [],
  exFactory: "",
  portRoute: "",
  orderStart: null,
  totalDays: 0,
  deliveryDate: null,
  fabricDefault: "",
  totalQuantity: "",
  construction: "",
  Buyer: { name: "", location: "" },
  Suppliers: [],
  sharedOrder: !!user.sharedOrder,
  sharedWith: user.sharedWith ?? null,
});

export default function SuppliersTab({ users, setUsers, showToast }) {
  const navigate = useNavigate();

  // ── Create form ──────────────────────────────────────────────────────────
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [category, setCategory] = useState("woven");
  const [country, setCountry] = useState("India");
  const [isBuyer, setIsBuyer] = useState(false);
  const [linkedSupplierUid, setLinkedSupplierUid] = useState("");
  const [sharedOrder, setSharedOrder] = useState(false);
  const [sharedWith, setSharedWith] = useState("");
  const [creating, setCreating] = useState(false);

  // ── Edit modal ───────────────────────────────────────────────────────────
  const [editingUser, setEditingUser] = useState(null);
  const [editName, setEditName] = useState("");
  const [editCategory, setEditCategory] = useState("woven");
  const [editCountry, setEditCountry] = useState("India");
  const [editIsBuyer, setEditIsBuyer] = useState(false);
  const [editLinkedSupplierUid, setEditLinkedSupplierUid] = useState("");
  const [editSharedOrder, setEditSharedOrder] = useState(false);
  const [editSharedWith, setEditSharedWith] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [editMessage, setEditMessage] = useState("");
  const [editMessageType, setEditMessageType] = useState("success");

  // ── Delete state ─────────────────────────────────────────────────────────
  const [confirmDeleteUser, setConfirmDeleteUser] = useState(null);
  const [deletingUid, setDeletingUid] = useState(null);

  // ── Table state ───────────────────────────────────────────────────────────
  const [sortDir, setSortDir] = useState("asc");
  const [filterCategory, setFilterCategory] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  // ── Derived data ──────────────────────────────────────────────────────────
  const supplierUsers = useMemo(
    () =>
      users.filter(
        (u) =>
          ![
            "merchandiser",
            "quality-control",
            "designer",
            "logistics",
            "branding-retail",
          ].includes(u.category),
      ),
    [users],
  );
  const supplierOptions = useMemo(
    () => supplierUsers.filter((u) => !u.isBuyer),
    [supplierUsers],
  );

  const counts = useMemo(
    () => ({
      woven: supplierUsers.filter((u) => u.category === "woven").length,
      knitted: supplierUsers.filter((u) => u.category === "knitted").length,
      nonWoven: supplierUsers.filter((u) => u.category === "non-woven").length,
      common: supplierUsers.filter((u) => u.category === "common").length,
      total: supplierUsers.length,
    }),
    [supplierUsers],
  );

  const filteredUsers = useMemo(
    () =>
      supplierUsers
        .filter(
          (u) => filterCategory === "all" || u.category === filterCategory,
        )
        .sort((a, b) => {
          const na = (a.name || "").toLowerCase();
          const nb = (b.name || "").toLowerCase();
          return sortDir === "asc"
            ? na.localeCompare(nb)
            : nb.localeCompare(na);
        }),
    [supplierUsers, filterCategory, sortDir],
  );
  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const paginatedUsers = useMemo(
    () =>
      filteredUsers.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE,
      ),
    [filteredUsers, currentPage],
  );

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleCreateUser = useCallback(async () => {
    if (!username.trim() || !password) {
      showToast("Please fill in both fields.", "error");
      return;
    }
    if (isBuyer && !linkedSupplierUid) {
      showToast("Please select a linked supplier for this buyer.", "error");
      return;
    }
    if (sharedOrder && !sharedWith) {
      showToast(
        "Please select a co-supplier to share the order with.",
        "error",
      );
      return;
    }
    setCreating(true);
    let secondaryApp = null;
    try {
      const email = `${username.trim()}@synaptex.app`;
      secondaryApp = initializeApp(firebaseConfig, "secondary");
      const secondaryAuth = getAuth(secondaryApp);
      const { user: fbUser } = await createUserWithEmailAndPassword(
        secondaryAuth,
        email,
        password,
      );
      const uid = fbUser.uid;
      const docData = {
        name: username.trim(),
        isAdmin,
        category,
        country,
        isBuyer,
        linkedSupplierUid: isBuyer ? linkedSupplierUid : null,
        sharedOrder,
        sharedWith: sharedOrder ? sharedWith : null,
      };
      await setDoc(doc(db, "users", uid), docData);
      if (!isAdmin) {
        await addDoc(collection(db, "users", uid, "orders"), {
          ...blankOrderProfile({
            sharedOrder,
            sharedWith: sharedOrder ? sharedWith : null,
          }),
          createdAt: serverTimestamp(),
        });
      }
      await secondaryAuth.signOut();

      showToast(`User "${username.trim()}" created successfully.`);
      setUsername("");
      setPassword("");
      setIsAdmin(false);
      setCategory("woven");
      setCountry("India");
      setIsBuyer(false);
      setLinkedSupplierUid("");
      setSharedOrder(false);
      setSharedWith("");
    } catch (err) {
      const msgs = {
        "auth/email-already-in-use": "That username is already taken.",
        "auth/weak-password": "Password must be at least 6 characters.",
        "auth/invalid-email": "Invalid username format.",
      };
      showToast(msgs[err.code] || err.message, "error");
    } finally {
      if (secondaryApp) await deleteApp(secondaryApp).catch(() => {});
      setCreating(false);
    }
  }, [
    username,
    password,
    isAdmin,
    category,
    country,
    isBuyer,
    linkedSupplierUid,
    sharedOrder,
    sharedWith,
    showToast,
    setUsers,
  ]);

  const handleOpenEdit = useCallback((user) => {
    setEditingUser(user);
    setEditName(user.name || "");
    setEditCategory(user.category || "woven");
    setEditCountry(user.country || "India");
    setEditIsBuyer(!!user.isBuyer);
    setEditLinkedSupplierUid(user.linkedSupplierUid || "");
    setEditSharedOrder(!!user.sharedOrder);
    setEditSharedWith(user.sharedWith || "");
    setEditMessage("");
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editingUser) return;
    if (editIsBuyer && !editLinkedSupplierUid) {
      setEditMessage("Please select a linked supplier.");
      setEditMessageType("error");
      return;
    }
    if (editSharedOrder && !editSharedWith) {
      setEditMessage("Please select a co-supplier.");
      setEditMessageType("error");
      return;
    }
    setEditSaving(true);
    try {
      const updatedFields = {
        name: editName.trim() || editingUser.name,
        category: editCategory,
        country: editCountry,
        isBuyer: editIsBuyer,
        linkedSupplierUid: editIsBuyer ? editLinkedSupplierUid : null,
      };
      await updateDoc(doc(db, "users", editingUser.uid), updatedFields);
      setUsers((prev) =>
        prev.map((u) =>
          u.uid === editingUser.uid ? { ...u, ...updatedFields } : u,
        ),
      );
      setEditMessage("Saved.");
      setEditMessageType("success");
      setTimeout(() => {
        setEditingUser(null);
        setEditMessage("");
      }, 1200);
    } catch (err) {
      setEditMessage(err.message);
      setEditMessageType("error");
    } finally {
      setEditSaving(false);
    }
  }, [
    editingUser,
    editName,
    editCategory,
    editCountry,
    editIsBuyer,
    editLinkedSupplierUid,
    editSharedOrder,
    editSharedWith,
    setUsers,
  ]);

  const closeEdit = useCallback(() => {
    setEditingUser(null);
    setEditMessage("");
  }, []);

  const handleDeleteUser = useCallback(
    async (user) => {
      setDeletingUid(user.uid);
      try {
        const auth = getAuth();
        const idToken = await auth.currentUser.getIdToken();

        const res = await fetch("/.netlify/functions/delete-user", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ uid: user.uid }),
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Delete failed");

        setUsers((prev) => prev.filter((u) => u.uid !== user.uid));
        showToast(`"${user.name}" deleted successfully.`);
      } catch (err) {
        showToast(err.message, "error");
      } finally {
        setDeletingUid(null);
        setConfirmDeleteUser(null);
      }
    },
    [showToast, setUsers],
  );

  return (
    <>
      {/* ── Overview ── */}
      <h2 className={styles.sectionTitle}>Supplier Overview</h2>
      <div className={styles.statsRow}>
        <StatCard form label="New Supplier">
          <input
            className={styles.input}
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <input
            className={styles.input}
            placeholder="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <SelectWrapper
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <optgroup label="Supplier">
              <option value="woven">Woven</option>
              <option value="knitted">Knitted</option>
              <option value="non-woven">Non-Woven</option>
              <option value="common">Common</option>
            </optgroup>
            <optgroup label="Team">
              <option value="merchandiser">Merchandiser</option>
              <option value="quality-control">Quality Control</option>
              <option value="designer">Designer</option>
              <option value="logistics">Logistics</option>
              <option value="branding-retail">Branding &amp; Retail</option>
            </optgroup>
          </SelectWrapper>

          <SelectWrapper
            value={country}
            onChange={(e) => setCountry(e.target.value)}
          >
            {COUNTRIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </SelectWrapper>

          <Toggle
            label="Admin Access"
            checked={isAdmin}
            onChange={() => setIsAdmin((v) => !v)}
          />
          <Toggle
            label="Is Buyer"
            checked={isBuyer}
            onChange={() => {
              setIsBuyer((v) => !v);
              setLinkedSupplierUid("");
            }}
          />

          <div
            className={`${styles.conditionalField} ${isBuyer ? styles.conditionalFieldVisible : ""}`}
          >
            <SelectWrapper
              value={linkedSupplierUid}
              onChange={(e) => setLinkedSupplierUid(e.target.value)}
            >
              <option value="">— Select Supplier —</option>
              {supplierOptions.map((u) => (
                <option key={u.uid} value={u.uid}>
                  {u.name}
                </option>
              ))}
            </SelectWrapper>
          </div>

          <Toggle
            label="Shared Order"
            checked={sharedOrder}
            onChange={() => {
              setSharedOrder((v) => !v);
              setSharedWith("");
            }}
          />

          <div
            className={`${styles.conditionalField} ${sharedOrder ? styles.conditionalFieldVisible : ""}`}
          >
            <SelectWrapper
              value={sharedWith}
              onChange={(e) => setSharedWith(e.target.value)}
            >
              <option value="">— Select Co-Supplier —</option>
              {supplierOptions.map((u) => (
                <option key={u.uid} value={u.uid}>
                  {u.name}
                </option>
              ))}
            </SelectWrapper>
          </div>

          <button
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={handleCreateUser}
            disabled={creating}
          >
            {creating ? "Creating…" : "Create Supplier"}
          </button>
        </StatCard>

        <StatCard
          label="Woven"
          value={counts.woven}
          sub="Woven fabric suppliers"
        />
        <StatCard
          label="Knitted"
          value={counts.knitted}
          sub="Knit fabric suppliers"
        />
        <StatCard
          label="Non-Woven"
          value={counts.nonWoven}
          sub="Non-woven suppliers"
        />
        <StatCard
          label="Common"
          value={counts.common}
          sub="Dyeing · Printing · Embroidery"
        />
        <StatCard
          label="Total Suppliers"
          value={counts.total}
          sub="All registered suppliers"
        />
      </div>

      <div className={styles.rule} />

      {/* ── Table ── */}
      <h2 className={styles.sectionTitle}>Suppliers</h2>
      {supplierUsers.length === 0 ? (
        <p className={styles.stateText}>No suppliers found.</p>
      ) : (
        <>
          <div className={styles.tableWrapper}>
            <div className={styles.tableControls}>
              <div className={styles.tableFilterGroup}>
                <span className={styles.tableFilterLabel}>
                  Filter by Category
                </span>
                <SelectWrapper
                  value={filterCategory}
                  selectClassName={styles.tableFilterSelect}
                  onChange={(e) => {
                    setFilterCategory(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="all">All</option>
                  <option value="woven">Woven</option>
                  <option value="knitted">Knitted</option>
                  <option value="non-woven">Non-Woven</option>
                  <option value="common">Common</option>
                </SelectWrapper>
              </div>
            </div>

            <div className={styles.tableScroll}>
              <table className={styles.table}>
                <thead>
                  <tr className={styles.tableHeadRow}>
                    <th className={styles.th}>#</th>
                    <th className={styles.th}>
                      <button
                        className={styles.sortBtn}
                        onClick={() => {
                          setSortDir((d) => (d === "asc" ? "desc" : "asc"));
                          setCurrentPage(1);
                        }}
                      >
                        Name{" "}
                        <span className={styles.sortIcon}>
                          {sortDir === "asc" ? "↑" : "↓"}
                        </span>
                      </button>
                    </th>
                    <th className={styles.th}>Category</th>
                    <th className={styles.th}>Country</th>
                    <th className={styles.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedUsers.map((user, index) => (
                    <tr key={user.uid} className={styles.tableRow}>
                      <td className={styles.td}>
                        {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                      </td>
                      <td className={styles.td}>
                        <button
                          className={styles.supplierLink}
                          onClick={() => navigate(`/plm/${user.uid}`)}
                        >
                          {user.name}
                        </button>
                      </td>
                      <td className={styles.td}>
                        <CategoryBadge category={user.category} />
                      </td>
                      <td className={styles.td}>{user.country || "—"}</td>
                      <td className={styles.td}>
                        <button
                          className={styles.editBtn}
                          onClick={() => handleOpenEdit(user)}
                          title="Edit supplier"
                        >
                          ✎
                        </button>
                        <button
                          className={`${styles.editBtn} ${styles.deleteBtn}`}
                          onClick={() => setConfirmDeleteUser(user)}
                          title="Delete supplier"
                          disabled={deletingUid === user.uid}
                        >
                          {deletingUid === user.uid ? "…" : "🗑"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </>
      )}

      {/* ── Delete Confirm Modal ── */}
      {confirmDeleteUser && (
        <Modal
          title="Delete Supplier"
          onClose={() => setConfirmDeleteUser(null)}
        >
          <p style={{ marginBottom: 16 }}>
            Permanently delete <strong>{confirmDeleteUser.name}</strong>? This
            will remove their Firestore data and Firebase Auth account. This
            cannot be undone.
          </p>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className={`${styles.btn} ${styles.btnDanger}`}
              onClick={() => handleDeleteUser(confirmDeleteUser)}
              disabled={deletingUid === confirmDeleteUser.uid}
            >
              {deletingUid === confirmDeleteUser.uid
                ? "Deleting…"
                : "Yes, Delete"}
            </button>
            <button
              className={styles.btn}
              onClick={() => setConfirmDeleteUser(null)}
            >
              Cancel
            </button>
          </div>
        </Modal>
      )}

      {/* ── Edit Modal ── */}
      {editingUser && (
        <Modal title={`Edit — ${editingUser.name}`} onClose={closeEdit}>
          <div className={styles.formInner}>
            <input
              className={styles.input}
              placeholder="Name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
            />

            <SelectWrapper
              value={editCategory}
              onChange={(e) => setEditCategory(e.target.value)}
            >
              <optgroup label="Supplier">
                <option value="woven">Woven</option>
                <option value="knitted">Knitted</option>
                <option value="non-woven">Non-Woven</option>
                <option value="common">Common</option>
              </optgroup>
              <optgroup label="Team">
                <option value="merchandiser">Merchandiser</option>
                <option value="quality-control">Quality Control</option>
                <option value="designer">Designer</option>
                <option value="logistics">Logistics</option>
                <option value="branding-retail">Branding &amp; Retail</option>
              </optgroup>
            </SelectWrapper>

            <SelectWrapper
              value={editCountry}
              onChange={(e) => setEditCountry(e.target.value)}
            >
              {COUNTRIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </SelectWrapper>

            <Toggle
              label="Is Buyer"
              checked={editIsBuyer}
              onChange={() => {
                setEditIsBuyer((v) => !v);
                setEditLinkedSupplierUid("");
              }}
            />

            <div
              className={`${styles.conditionalField} ${editIsBuyer ? styles.conditionalFieldVisible : ""}`}
            >
              <SelectWrapper
                value={editLinkedSupplierUid}
                onChange={(e) => setEditLinkedSupplierUid(e.target.value)}
              >
                <option value="">Select Supplier</option>
                {supplierOptions
                  .filter((u) => u.uid !== editingUser.uid)
                  .map((u) => (
                    <option key={u.uid} value={u.uid}>
                      {u.name}
                    </option>
                  ))}
              </SelectWrapper>
            </div>

            <button
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={handleSaveEdit}
              disabled={editSaving}
            >
              {editSaving ? "Saving…" : "Save Changes"}
            </button>

            {editMessage && (
              <p
                className={`${styles.modalMessage} ${editMessageType === "success" ? styles.modalMessageSuccess : styles.modalMessageError}`}
              >
                {editMessage}
              </p>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}
